"use client";

import { IAddressSuggestionResponse } from "@/lib/services/types";
import React, {useEffect, useMemo, useRef, useState} from "react";
import useClickOutside from "@/hooks/useClickOutside";
import { BiMap, BiSearch} from "react-icons/bi";
import { GoogleMap, Marker, Circle, Autocomplete, useLoadScript } from "@react-google-maps/api";

const MapIcon = React.memo(() => {
  return (
    <BiMap size={22} />
  );
});

MapIcon.displayName = "MapIcon";

const SearchIcon = React.memo(() => <BiSearch size={22} />);

SearchIcon.displayName = "SearchIcon";

type Props = {
  options?: Array<{ label: string; value: string }>;
  searchInputIcon?: "map" | "search";
  placeholder?: string;
  showMap?: boolean;
  mapConfigs?: {
    zoom?: number;
    zoomControl?: boolean;
    scrollWheelZoom?: boolean;
    dragging?: boolean;
    forceDefaultBound?: boolean;
    icon?: "house" | "pin";
    radius?: number;
  };
  value?: string;
  onSelectAddress: (address: IAddressSuggestionResponse) => void;
};

const defaultMapConfigs: Props["mapConfigs"] = {
  zoom: 13,
  zoomControl: false,
  scrollWheelZoom: false,
  dragging: false,
  forceDefaultBound: false,
  icon: "pin",
  radius: 1000,
};

const defaultCenter = { lat: 46.8182, lng: 8.2275 };

const libraries: Array<"places"> = ["places"];

const QuestionAddressInput = React.memo(({ options, searchInputIcon = "map", value, showMap, placeholder = "e.g. Rue du Rhône 7", mapConfigs = defaultMapConfigs, onSelectAddress }: Props) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "",
    libraries,
  });

  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [isFocus, setIsFocus] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState(value || "");
  const [position, setPosition] = useState(defaultCenter);
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useClickOutside(mainContainerRef, () => setIsFocus(false));

  const onLoad = (autocomplete: google.maps.places.Autocomplete) => setAutocomplete(autocomplete);

  const onPlaceChanged = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.geometry?.location) {
        const newPosition = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
        setPosition(newPosition);
        const addressData: IAddressSuggestionResponse = {
          full_address: place.formatted_address || "",
          //lat: place.geometry.location.lat(),
          //lng: place.geometry.location.lng(),
        };
        setSearchQuery(place.formatted_address || "");
        onSelectAddress(addressData);
      }
    }
  };

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const filteredOptions = useMemo(() => {
    if (!options) return [];
    return options.filter((opt) =>
        opt.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        opt.value.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [options, searchQuery]);

  const mapContainerStyle = {
    width: "100%",
    height: "256px",
    borderRadius: "10px",
  };

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading maps...</div>;

  return (
      <div style={{ opacity: isVisible ? 1 : 0, transition: "opacity 0.5s ease-in" }}>
        <div className="relative">
          <div ref={mainContainerRef} className="w-full">
            <div className="relative w-full">
              <div className="absolute left-0 top-0 bottom-0 flex items-center pl-4">
                {searchInputIcon === "map" ? <MapIcon /> : <SearchIcon />}
              </div>
              {isLoaded && (
                  <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged} options={{ types: ["geocode"], componentRestrictions: { country: "ch" } }}>
                    <input
                        className="w-full rounded-lg border px-4 py-2 pl-12"
                        placeholder={placeholder}
                        ref={inputRef}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => setIsFocus(true)}
                    />
                  </Autocomplete>
              )}
            </div>
          </div>
          {showMap && isLoaded && (
              <div className="relative h-64 mt-2 rounded-md bg-white">
                <GoogleMap mapContainerStyle={mapContainerStyle} center={position} zoom={mapConfigs.zoom} options={{ mapTypeControl: false, streetViewControl: false, fullscreenControl: false }}>
                  <Marker position={position} />
                  <Circle center={position} radius={mapConfigs.radius} options={{ fillColor: "blue", fillOpacity: 0.2, strokeColor: "blue", strokeOpacity: 0.5, strokeWeight: 1 }} />
                </GoogleMap>
              </div>
          )}
        </div>
      </div>
  );
});

QuestionAddressInput.displayName = "QuestionAddressInput";

export default QuestionAddressInput;
